import { PDFDocument } from 'pdf-lib';
import { excelFileToExtractText, isExcelFileName } from './demPoExcel';
import {
  countVendorSkuRows,
  tryExtractConsolidationOrders,
  tryExtractOrdersFromRowMatrix,
  vendorSkuRowKey,
} from './demPoExcelExtract';
import {
  parseEmailFile,
  resolveEmailToProcessingFile,
  htmlTablesToCsvText,
  countVendorSkusInHtml,
} from './demPoEmailParse';
import type { DemPoExtractOrder, DemPoExtractRow } from '../types/dem';
import { parseDeliveryDateDmy } from './demDeliveryDate';
import { refinePoExtractRow } from './demPoItemRefine';
import {
  cancelAllExtractRequests,
  registerExtractAbortController,
  unregisterExtractAbortController,
} from './demExtractAbort';

export type PoSourcePreview =
  | { kind: 'pdf'; url: string }
  | { kind: 'excel'; fileName: string; rows: string[][] }
  | { kind: 'html'; url: string; label?: string }
  | {
      kind: 'email';
      subject?: string;
      meta: Array<{ label: string; value: string }>;
      body: string;
    }
  | { kind: 'text'; label: string; text: string };

export type PreparedPoSource = {
  file: File;
  /** Prefer this over buildPoSourcePreview(file) when set (emails). */
  preview?: PoSourcePreview;
};

function htmlPreviewUrl(html: string): string {
  const trimmed = html.trim();
  const hasDoc = /<html[\s>]/i.test(trimmed) || /<!DOCTYPE/i.test(trimmed);
  const doc = hasDoc
    ? trimmed
    : `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
body{font-family:Segoe UI,system-ui,sans-serif;font-size:13px;line-height:1.45;color:#1f2937;margin:0;padding:14px;background:#fff}
table{border-collapse:collapse;width:100%;margin:10px 0;font-size:12px}
th,td{border:1px solid #dbe2ea;padding:6px 8px;vertical-align:top;text-align:left}
th{background:#f3f5f8;font-weight:700}
img{max-width:100%;height:auto}
</style></head><body>${trimmed}</body></html>`;
  return URL.createObjectURL(new Blob([doc], { type: 'text/html' }));
}

/** Turn OCR / plain email dumps into a readable card (never show RAW EMAIL banner). */
export function buildEmailTextPreview(raw: string, subjectHint = ''): PoSourcePreview {
  const cleaned = String(raw ?? '')
    .replace(/^RAW EMAIL TEXT \(NO TABLES\):\s*/i, '')
    .replace(/^\uFEFF/, '')
    .trim();

  const meta: Array<{ label: string; value: string }> = [];
  const lines = cleaned.split(/\r?\n/);
  let bodyStart = 0;
  for (let i = 0; i < Math.min(lines.length, 20); i += 1) {
    const line = lines[i] ?? '';
    const match = line.match(/^(From|Sent|To|Cc|Subject|Date)\s*:\s*(.+)$/i);
    if (!match) {
      if (line.trim() === '' && meta.length > 0) {
        bodyStart = i + 1;
        break;
      }
      if (meta.length > 0 && line.trim()) {
        bodyStart = i;
        break;
      }
      continue;
    }
    meta.push({ label: match[1]!, value: match[2]!.trim() });
    bodyStart = i + 1;
  }

  const subject =
    meta.find((m) => m.label.toLowerCase() === 'subject')?.value ||
    subjectHint.trim() ||
    undefined;
  const body = lines.slice(bodyStart).join('\n').trim() || cleaned;

  return { kind: 'email', subject, meta, body };
}

function attachmentExt(fileName: string): string {
  return fileName.split('.').pop()?.toLowerCase() ?? '';
}

function pickLargestAttachment(
  attachments: Array<{ fileName: string; mimeType: string; blob: Blob }>,
  match: (item: { fileName: string; mimeType: string }) => boolean,
): { fileName: string; mimeType: string; blob: Blob } | null {
  const matches = attachments.filter(match);
  if (matches.length === 0) return null;
  return [...matches].sort((a, b) => b.blob.size - a.blob.size)[0] ?? null;
}

function pickPdfAttachment(
  attachments: Array<{ fileName: string; mimeType: string; blob: Blob }>,
): { fileName: string; mimeType: string; blob: Blob } | null {
  return pickLargestAttachment(
    attachments,
    (item) => attachmentExt(item.fileName) === 'pdf' || item.mimeType === 'application/pdf',
  );
}

function pickExcelAttachment(
  attachments: Array<{ fileName: string; mimeType: string; blob: Blob }>,
): { fileName: string; mimeType: string; blob: Blob } | null {
  return pickLargestAttachment(attachments, (item) => {
    const ext = attachmentExt(item.fileName);
    return ext === 'xlsx' || ext === 'xls' || isExcelFileName(item.fileName);
  });
}

function pickHtmlAttachment(
  attachments: Array<{ fileName: string; mimeType: string; blob: Blob }>,
): { fileName: string; mimeType: string; blob: Blob } | null {
  return pickLargestAttachment(attachments, (item) => {
    const ext = attachmentExt(item.fileName);
    return ext === 'htm' || ext === 'html' || /html/i.test(item.mimeType);
  });
}

async function buildPreviewFromEmailParse(
  parsed: {
    subject: string;
    attachments: Array<{ fileName: string; mimeType: string; blob: Blob }>;
    bodyText: string;
    bodyHtml: string;
  },
): Promise<PoSourcePreview | undefined> {
  // Prefer real PO attachments over HTML notification bodies (e.g. Paradise / FoodMarketHub).
  const pdfAtt = pickPdfAttachment(parsed.attachments);
  if (pdfAtt) {
    return buildPoSourcePreview(
      new File([pdfAtt.blob], pdfAtt.fileName, {
        type: pdfAtt.mimeType || 'application/pdf',
      }),
    );
  }

  const excelAtt = pickExcelAttachment(parsed.attachments);
  if (excelAtt) {
    return buildPoSourcePreview(
      new File([excelAtt.blob], excelAtt.fileName, {
        type: excelAtt.mimeType || 'application/octet-stream',
      }),
    );
  }

  const htmlAtt = pickHtmlAttachment(parsed.attachments);
  if (htmlAtt) {
    const html = await htmlAtt.blob.text();
    if (html.trim()) {
      return {
        kind: 'html',
        url: htmlPreviewUrl(html),
        label: htmlAtt.fileName,
      };
    }
  }

  if (parsed.bodyHtml?.trim()) {
    return {
      kind: 'html',
      url: htmlPreviewUrl(parsed.bodyHtml),
      label: parsed.subject || 'Email',
    };
  }

  const text = parsed.bodyText?.trim();
  if (text) {
    return buildEmailTextPreview(text, parsed.subject);
  }
  return undefined;
}

function parseQty(raw: unknown): number {
  const cleaned = String(raw ?? '0').replace(/[^\d.]/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function normalizeExtractRow(row: Record<string, unknown>): DemPoExtractRow | null {
  const desc = String(row.description ?? row.Description ?? row.product ?? '').trim();
  const items = String(row.items ?? row.Items ?? row.item_code ?? row.Code ?? '').trim();
  const qty = parseQty(row.quantity ?? row.Quantity ?? row.qty);
  if (!desc && !items) return null;

  const uom = String(row.uom ?? row.UOM ?? row.unit ?? '').trim();
  const unit_price = String(row.unit_price ?? row.price ?? '').trim();

  return refinePoExtractRow({ items, description: desc, quantity: String(qty), uom, unit_price });
}

function normalizePoNumber(raw: string): string {
  const line = raw.split(/\r?\n/)[0]?.trim() ?? '';
  if (!line) return '';
  const poMatch = line.match(/PO\s*SP\s*[\d\s-]+/i);
  if (poMatch) return poMatch[0].replace(/\s+/g, ' ').trim();
  return line.split(/Issue Date:/i)[0]?.trim() ?? line;
}

function normalizeExtractOrder(raw: Record<string, unknown>, fileName: string): DemPoExtractOrder | null {
  const headerRaw = (raw.header ?? {}) as Record<string, unknown>;
  const rowsRaw = Array.isArray(raw.rows) ? raw.rows : [];

  const rows: DemPoExtractRow[] = [];
  for (const row of rowsRaw) {
    const normalized = normalizeExtractRow(row as Record<string, unknown>);
    if (!normalized) continue;
    const qty = parseQty(normalized.quantity);
    if (qty <= 0) continue;
    rows.push(normalized);
  }

  if (rows.length === 0) return null;

  const header = {
    company_name: String(headerRaw.company_name ?? '').trim(),
    company_address: String(headerRaw.company_address ?? '').trim(),
    po_number: normalizePoNumber(String(headerRaw.po_number ?? '')),
    delivery_date: parseDeliveryDateDmy(String(headerRaw.delivery_date ?? '').trim()),
    source_file: fileName,
  };

  return { header, rows };
}

function orderMergeKey(header: DemPoExtractOrder['header']): string {
  return [
    header.po_number?.trim().toUpperCase() ?? '',
    header.company_name?.trim().toUpperCase() ?? '',
    header.delivery_date?.trim() ?? '',
  ].join('|');
}

function mergePoExtractOrders(orders: DemPoExtractOrder[]): DemPoExtractOrder[] {
  if (orders.length <= 1) return orders;

  const groups = new Map<string, DemPoExtractOrder>();
  for (const order of orders) {
    const key = orderMergeKey(order.header);
    const existing = groups.get(key);
    if (!existing) {
      groups.set(key, {
        header: { ...order.header },
        rows: [...order.rows],
      });
      continue;
    }

    const seen = new Set(existing.rows.map(vendorSkuRowKey));
    for (const row of order.rows) {
      const key = vendorSkuRowKey(row);
      if (seen.has(key)) continue;
      seen.add(key);
      existing.rows.push(row);
    }

    for (const field of ['company_name', 'company_address', 'po_number', 'delivery_date'] as const) {
      if (!existing.header[field]?.trim() && order.header[field]?.trim()) {
        existing.header[field] = order.header[field];
      }
    }
  }

  return [...groups.values()];
}

function normalizeOrders(ordersRaw: unknown, fileName: string): DemPoExtractOrder[] {
  const orders = Array.isArray(ordersRaw) ? ordersRaw : [];
  const normalized: DemPoExtractOrder[] = [];
  for (const order of orders) {
    if (!order || typeof order !== 'object') continue;
    const parsed = normalizeExtractOrder(order as Record<string, unknown>, fileName);
    if (parsed) normalized.push(dedupeExtractOrderRows(parsed));
  }
  return normalized;
}

/** Second-pass: drop AI table repeats, keep legitimate re-ordered lines. */
function dedupeExtractOrderRows(order: DemPoExtractOrder): DemPoExtractOrder {
  const rows = order.rows;
  if (rows.length < 6) return order;

  const fingerprints = rows.map((row) => vendorSkuRowKey(row));

  // AI sometimes returns the whole line list twice (or a long tail copy of the head).
  // Legitimate re-orders stay — we only strip a repeated block, never unique lines.
  for (let n = Math.floor(rows.length / 2); n >= 3; n -= 1) {
    const head = fingerprints.slice(0, n);
    const tail = fingerprints.slice(rows.length - n);
    if (head.every((key, i) => key === tail[i])) {
      return { header: order.header, rows: rows.slice(0, rows.length - n) };
    }
  }

  return order;
}

/** Match desktop PO extraction: first N pages only. */
const PO_EXTRACT_MAX_PAGES = 2;

async function savePdfPages(src: PDFDocument, pageIndices: number[]): Promise<Uint8Array> {
  const out = await PDFDocument.create();
  const pages = await out.copyPages(src, pageIndices);
  for (const page of pages) out.addPage(page);
  return out.save();
}

/** Desktop always rebuilds a trimmed PDF (fitz insert_pdf) instead of sending the raw file. */
export async function preparePdfBytesForExtraction(bytes: Uint8Array): Promise<Uint8Array> {
  try {
    const src = await PDFDocument.load(bytes, { ignoreEncryption: true });
    const pageCount = src.getPageCount();
    const keep = Math.min(PO_EXTRACT_MAX_PAGES, pageCount);
    if (keep >= pageCount) return bytes;
    return savePdfPages(src, Array.from({ length: keep }, (_, i) => i));
  } catch {
    return bytes;
  }
}

function isWeakCompanyName(name: string): boolean {
  const normalized = name.trim().toLowerCase();
  return !normalized || normalized === 'unknown' || normalized === 'n/a' || normalized === 'none';
}

function mergeOrderRowsInto(
  target: DemPoExtractOrder,
  source: DemPoExtractOrder,
  options?: { preserveEmptyAddress?: boolean },
): void {
  const seen = new Set(target.rows.map(vendorSkuRowKey));
  for (const row of source.rows) {
    const key = vendorSkuRowKey(row);
    if (seen.has(key)) continue;
    seen.add(key);
    target.rows.push(row);
  }

  for (const field of ['company_name', 'company_address', 'po_number', 'delivery_date'] as const) {
    if (field === 'company_address' && options?.preserveEmptyAddress) continue;
    if (!target.header[field]?.trim() && source.header[field]?.trim()) {
      target.header[field] = source.header[field];
    }
  }
}

function pickPrimaryOrder(orders: DemPoExtractOrder[]): DemPoExtractOrder {
  return [...orders].sort((a, b) => {
    const aWeak = isWeakCompanyName(a.header.company_name);
    const bWeak = isWeakCompanyName(b.header.company_name);
    if (aWeak !== bWeak) return aWeak ? 1 : -1;

    const aAddr = a.header.company_address?.trim() ? 1 : 0;
    const bAddr = b.header.company_address?.trim() ? 1 : 0;
    if (aAddr !== bAddr) return bAddr - aAddr;

    return b.rows.length - a.rows.length;
  })[0]!;
}

/** Page 2+ often has rows only; merge into the order that has the PO header. */
function collapseSameDocumentPages(orders: DemPoExtractOrder[]): DemPoExtractOrder[] {
  const merged = mergePoExtractOrders(orders);
  if (merged.length <= 1) return merged.map(dedupeExtractOrderRows);

  const byPo = new Map<string, DemPoExtractOrder[]>();
  const orphans: DemPoExtractOrder[] = [];

  for (const order of merged) {
    const po = order.header.po_number?.trim().toUpperCase() ?? '';
    if (!po) {
      orphans.push(order);
      continue;
    }
    const list = byPo.get(po) ?? [];
    list.push(order);
    byPo.set(po, list);
  }

  const result: DemPoExtractOrder[] = [];

  for (const group of byPo.values()) {
    if (group.length === 1) {
      result.push(dedupeExtractOrderRows(group[0]!));
      continue;
    }

    const primary = pickPrimaryOrder(group);
    const combined: DemPoExtractOrder = {
      header: { ...primary.header },
      rows: [...primary.rows],
    };
    for (const order of group) {
      if (order === primary) continue;
      mergeOrderRowsInto(combined, order);
    }
    result.push(dedupeExtractOrderRows(combined));
  }

  if (result.length === 1 && orphans.length > 0) {
    for (const orphan of orphans) {
      mergeOrderRowsInto(result[0]!, orphan);
    }
    return [dedupeExtractOrderRows(result[0]!)];
  }

  return [...result, ...orphans.map(dedupeExtractOrderRows)];
}

export async function trimPdfToFirstPages(file: File, maxPages = PO_EXTRACT_MAX_PAGES): Promise<Uint8Array> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  try {
    const src = await PDFDocument.load(bytes, { ignoreEncryption: true });
    const pageCount = src.getPageCount();
    const keep = Math.min(maxPages, pageCount);
    if (keep >= pageCount) return bytes;
    return savePdfPages(src, Array.from({ length: keep }, (_, i) => i));
  } catch {
    return bytes;
  }
}

const EXTRACT_TIMEOUT_MS = 180_000;

let activeExtractController: AbortController | null = null;

export function cancelActivePoExtract(): void {
  cancelAllExtractRequests();
  activeExtractController = null;
}

function getProxyKey(): string {
  const proxyKey = import.meta.env.VITE_DEM_EMAIL_PROXY_KEY?.trim();
  if (!proxyKey) {
    throw new Error('VITE_DEM_EMAIL_PROXY_KEY is not configured for PO extraction.');
  }
  return proxyKey;
}

function parseProxyError(text: string, status: number): string {
  let message = `Extraction failed (${status})`;
  try {
    const json = JSON.parse(text) as { error?: string; detail?: string; hint?: string };
    if (json.error) message = json.error;
    if (json.detail?.trim()) {
      message = `${message}: ${json.detail.trim().slice(0, 220)}`;
    }
    if (json.hint?.trim()) {
      message = `${message} (${json.hint.trim()})`;
    }
  } catch {
    if (text.trim()) message = text.trim().slice(0, 300);
  }
  return message;
}

async function callExtractProxyOnce(
  init: RequestInit,
  fileName: string,
): Promise<DemPoExtractOrder[]> {
  const controller = new AbortController();
  activeExtractController = controller;
  registerExtractAbortController(controller);
  const startedAt = Date.now();
  const timer = window.setTimeout(() => controller.abort(), EXTRACT_TIMEOUT_MS);
  try {
    const res = await fetch('/po-extract-api', {
      ...init,
      signal: controller.signal,
    });
    const text = await res.text();
    if (!res.ok) {
      throw new Error(parseProxyError(text, res.status));
    }
    const json = JSON.parse(text) as { orders?: unknown[] };
    return normalizeOrders(json.orders, fileName);
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') {
      const elapsed = Date.now() - startedAt;
      if (elapsed >= EXTRACT_TIMEOUT_MS - 1500) {
        throw new Error(
          `AI extraction timed out after ${Math.round(EXTRACT_TIMEOUT_MS / 1000)}s. Try again, or upload a clearer PDF/Excel.`,
        );
      }
      throw new Error('AI extraction was cancelled.');
    }
    throw e;
  } finally {
    window.clearTimeout(timer);
    unregisterExtractAbortController(controller);
    if (activeExtractController === controller) {
      activeExtractController = null;
    }
  }
}

async function callExtractProxyPayload(payload: Record<string, unknown>): Promise<DemPoExtractOrder[]> {
  const fileName = String(payload.fileName ?? 'document');
  return callExtractProxyOnce(
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-DEM-Email-Key': getProxyKey(),
      },
      body: JSON.stringify(payload),
    },
    fileName,
  );
}

async function callExtractProxyFile(file: File): Promise<DemPoExtractOrder[]> {
  const form = new FormData();
  form.append('file', file, file.name);
  form.append('fileName', file.name);
  return callExtractProxyOnce(
    {
      method: 'POST',
      headers: {
        'X-DEM-Email-Key': getProxyKey(),
      },
      body: form,
    },
    file.name,
  );
}

export async function extractPoOrdersFromPdf(file: File): Promise<DemPoExtractOrder[]> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const prepared = await preparePdfBytesForExtraction(bytes);
  const preparedFile = new File([prepared], file.name, { type: 'application/pdf' });

  let localOrders: DemPoExtractOrder[] | null = null;
  let localText = '';

  try {
    const pdfExtract = await import('./demPoPdfExtract');
    const local = await pdfExtract.extractOrdersFromPdfBytesLocally(bytes, file.name);
    localOrders = local.orders;
    localText = local.text;
  } catch {
    localOrders = null;
    localText = '';
  }

  // Successful local parse (SingHealth SOR / Buynow / Katrina) is the source of truth.
  // Merging AI on top duplicated every line whenever SKU/description fingerprints differed slightly.
  if ((localOrders?.reduce((sum, order) => sum + order.rows.length, 0) ?? 0) > 0) {
    return localOrders!;
  }

  const applyPdfHeaderOverrides = async (orders: DemPoExtractOrder[]): Promise<DemPoExtractOrder[]> => {
    if (!localText.trim()) return orders;
    try {
      const pdfExtract = await import('./demPoPdfExtract');
      return pdfExtract.applyPdfTextHeaderOverrides(orders, localText);
    } catch {
      return orders;
    }
  };

  // Prefer text AI when the PDF already has a usable text layer. Some Katrina /
  // complex PDFs hang or stall for minutes on Gemini's PDF path while text works.
  if (localText.trim().length >= 200) {
    try {
      const textOrders = await callExtractProxyPayload({
        fileName: file.name,
        textContent: localText,
      });
      if ((textOrders.reduce((sum, order) => sum + order.rows.length, 0) ?? 0) > 0) {
        return collapseSameDocumentPages(await applyPdfHeaderOverrides(textOrders));
      }
    } catch {
      /* fall through to PDF upload */
    }
  }

  return collapseSameDocumentPages(await applyPdfHeaderOverrides(await callExtractProxyFile(preparedFile)));
}

export async function extractPoOrdersFromText(fileName: string, textContent: string): Promise<DemPoExtractOrder[]> {
  return callExtractProxyPayload({ fileName, textContent });
}

function mergeExtractedOrders(
  primary: DemPoExtractOrder[],
  secondary: DemPoExtractOrder[],
  options?: { preserveEmptyAddress?: boolean },
): DemPoExtractOrder[] {
  if (primary.length === 0) return secondary;
  if (secondary.length === 0) return primary;

  const preserveEmptyAddress = options?.preserveEmptyAddress ?? false;
  const merged = primary.map((order) => ({ header: { ...order.header }, rows: [...order.rows] }));
  for (const order of secondary) {
    const target =
      merged.find((candidate) => orderMergeKey(candidate.header) === orderMergeKey(order.header)) ??
      (merged.length === 1 ? merged[0] : undefined);
    if (!target) {
      merged.push({ header: { ...order.header }, rows: [...order.rows] });
      continue;
    }
    mergeOrderRowsInto(target, order, { preserveEmptyAddress });
  }
  return merged;
}

export async function extractPoOrdersFromFile(file: File): Promise<DemPoExtractOrder[]> {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'pdf') {
    return extractPoOrdersFromPdf(file);
  }
  if (isExcelFileName(file.name) || ext === 'csv') {
    const { readExcelRowsForExtract } = await import('./demPoExcel');
    const rows = await readExcelRowsForExtract(file);

    // RE&S consolidation must stay local — AI is slow and re-pastes the HQ address.
    const consolidation = tryExtractConsolidationOrders(rows, file.name);
    if (consolidation && consolidation.length > 0) {
      return consolidation;
    }

    const expectedSkus = countVendorSkuRows(rows);
    const isEmailBodyCsv = /_Body\.csv$/i.test(file.name);

    let local = tryExtractOrdersFromRowMatrix(rows, file.name, { relaxed: isEmailBodyCsv });
    let extracted = local?.reduce((sum, order) => sum + order.rows.length, 0) ?? 0;
    if (local && expectedSkus > extracted) {
      const relaxed = tryExtractOrdersFromRowMatrix(rows, file.name, { relaxed: true });
      const relaxedCount = relaxed?.reduce((sum, order) => sum + order.rows.length, 0) ?? 0;
      if (relaxedCount > extracted) {
        local = relaxed;
        extracted = relaxedCount;
      }
    }

    if (local && local.length > 0 && (expectedSkus === 0 || extracted >= expectedSkus)) {
      return local;
    }

    const textContent = await excelFileToExtractText(file);
    const aiOrders = await extractPoOrdersFromText(file.name, textContent);
    if (local && local.length > 0) {
      const merged = mergeExtractedOrders(local, aiOrders, {
        preserveEmptyAddress: local.length > 1 || local.some((o) => !o.header.company_address?.trim()),
      });
      const mergedCount = merged.reduce((sum, order) => sum + order.rows.length, 0);
      if (mergedCount >= extracted && merged.length >= local.length) return merged;
      return local;
    }
    return aiOrders.length > 0 ? aiOrders : local ?? [];
  }
  if (ext === 'txt' || ext === 'htm' || ext === 'html') {
    let textContent = await file.text();
    if (ext === 'htm' || ext === 'html') {
      const tableCsv = htmlTablesToCsvText(textContent);
      if (tableCsv.trim()) {
        const csvFile = new File([tableCsv], file.name.replace(/\.html?$/i, '.csv'), {
          type: 'text/csv',
        });
        return extractPoOrdersFromFile(csvFile);
      }
      textContent = textContent
        .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, '')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/[ \t]+/g, ' ')
        .replace(/\n\s*\n/g, '\n')
        .trim();
    }
    // Plain-text email bodies often contain the RE&S consolidation table without HTML.
    const rows = plainTextToTableRows(textContent);
    if (rows.length >= 2) {
      const consolidation = tryExtractConsolidationOrders(rows, file.name);
      if (consolidation && consolidation.length > 0) return consolidation;
    }
    return extractPoOrdersFromText(file.name, textContent);
  }
  throw new Error(`Unsupported file type for AI extraction: .${ext}`);
}

/** Best-effort table from CSV / TSV / multi-space plain text (email body dumps). */
function plainTextToTableRows(text: string): string[][] {
  const cleaned = text.replace(/^RAW EMAIL TEXT \(NO TABLES\):\s*/i, '');
  const lines = cleaned
    .split(/\r?\n/)
    .map((line) => line.trimEnd())
    .filter((line) => line.trim());

  const rows: string[][] = [];
  for (const line of lines) {
    if (line.includes('\t')) {
      rows.push(line.split('\t').map((cell) => cell.trim()));
      continue;
    }
    if ((line.match(/,/g) ?? []).length >= 3) {
      // Lazy CSV — demPoExcel.parseCsvLine is sync-imported via dynamic when needed.
      rows.push(splitCsvishLine(line));
      continue;
    }
    if (/\s{2,}/.test(line)) {
      rows.push(line.split(/\s{2,}/).map((cell) => cell.trim()).filter(Boolean));
      continue;
    }
    rows.push([line.trim()]);
  }
  return rows;
}

function splitCsvishLine(line: string): string[] {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === ',' && !inQuotes) {
      cells.push(current.trim());
      current = '';
      continue;
    }
    current += ch;
  }
  cells.push(current.trim());
  return cells;
}

export async function buildPoSourcePreview(file: File): Promise<PoSourcePreview> {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'pdf') {
    const bytes = await file.arrayBuffer();
    const blob = new Blob([bytes], { type: 'application/pdf' });
    return { kind: 'pdf', url: URL.createObjectURL(blob) };
  }
  if (isExcelFileName(file.name) || ext === 'csv') {
    const { readExcelPreview } = await import('./demPoExcel');
    const preview = await readExcelPreview(file);
    return { kind: 'excel', fileName: preview.fileName, rows: preview.rows };
  }
  if (ext === 'htm' || ext === 'html') {
    const html = await file.text();
    return { kind: 'html', url: htmlPreviewUrl(html), label: file.name };
  }
  const text = await file.text();
  if (/^RAW EMAIL TEXT \(NO TABLES\):/i.test(text) || /^(From|Subject)\s*:/im.test(text)) {
    return buildEmailTextPreview(text, file.name);
  }
  return { kind: 'text', label: file.name, text };
}

export function buildCustomerKey(header: DemPoExtractOrder['header']): string {
  return `${header.company_name} ${header.company_address}`.trim();
}

export { formatDeliveryDateInput } from './demDeliveryDate';

export const PO_SOURCE_ACCEPT = '.pdf,.xlsx,.xls,.csv,.eml,.msg,application/pdf';

const PO_SOURCE_EXTENSIONS = new Set(['pdf', 'xlsx', 'xls', 'csv', 'eml', 'msg', 'txt']);

export function isPoSourceFile(file: File): boolean {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return PO_SOURCE_EXTENSIONS.has(ext);
}

export function pickPoSourceFile(files: FileList | File[] | null | undefined): File | null {
  if (!files) return null;
  for (const file of Array.from(files)) {
    if (isPoSourceFile(file)) return file;
  }
  return null;
}

export function dragEventHasFiles(dt: DataTransfer | null): boolean {
  return Boolean(dt?.types.includes('Files'));
}

async function bestEmailHtmlForPreviewAndTables(parsed: {
  attachments: Array<{ fileName: string; mimeType: string; blob: Blob }>;
  bodyHtml: string;
}): Promise<{ html: string; htmlSkuCount: number; csv: string }> {
  const candidates: string[] = [];
  if (parsed.bodyHtml?.trim()) candidates.push(parsed.bodyHtml);

  const htmlAtts = parsed.attachments.filter((item) => {
    const ext = item.fileName.split('.').pop()?.toLowerCase() ?? '';
    return ext === 'htm' || ext === 'html' || /html/i.test(item.mimeType);
  });
  for (const att of [...htmlAtts].sort((a, b) => b.blob.size - a.blob.size)) {
    try {
      const html = await att.blob.text();
      if (html.trim()) candidates.push(html);
    } catch {
      /* ignore */
    }
  }

  let bestCsv = '';
  let bestHtml = '';
  let bestSku = 0;
  for (const html of candidates) {
    const csv = htmlTablesToCsvText(html);
    const skuCount = countVendorSkusInHtml(html);
    if (csv && (!bestCsv || skuCount > bestSku || csv.length > bestCsv.length)) {
      bestCsv = csv;
      bestHtml = html;
      bestSku = skuCount;
    } else if (!bestHtml && html.trim()) {
      bestHtml = html;
      bestSku = Math.max(bestSku, skuCount);
    }
  }

  return { html: bestHtml, htmlSkuCount: bestSku, csv: bestCsv };
}

/** True for RE&S-style consolidation lists where the body table is the real PO source. */
function isLikelyConsolidationEmail(subject: string, bodyText: string): boolean {
  const hay = `${subject}\n${bodyText}`.toLowerCase();
  return (
    /consolidat/.test(hay) ||
    /order\s*list/.test(hay) ||
    /re\s*&\s*s/.test(hay) ||
    /\bres\b/.test(subject.toLowerCase())
  );
}

function emailBodyCsvFile(subject: string, csv: string): File {
  const safeSubject = subject.replace(/[^\w\-]+/g, '_').slice(0, 50) || 'Email_Body';
  return new File([csv], `${safeSubject}_Body.csv`, { type: 'text/csv' });
}

export async function preparePoSourceFile(file: File): Promise<PreparedPoSource> {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'eml' || ext === 'msg') {
    const parsed = await parseEmailFile(file);
    const resolved = await resolveEmailToProcessingFile(parsed);
    const preview = await buildPreviewFromEmailParse(parsed);
    const htmlSource = await bestEmailHtmlForPreviewAndTables(parsed);
    const attachmentExt = resolved.file.name.split('.').pop()?.toLowerCase() ?? '';
    const hasPdfAttachment = attachmentExt === 'pdf';
    const hasExcelAttachment = attachmentExt === 'csv' || attachmentExt === 'xlsx' || attachmentExt === 'xls';
    const consolidationHint = isLikelyConsolidationEmail(
      parsed.subject ?? '',
      parsed.bodyText ?? '',
    );

    if (htmlSource.csv) {
      const csv = htmlSource.csv;
      const htmlSkuCount = htmlSource.htmlSkuCount;
      const { readExcelRowsForExtract } = await import('./demPoExcel');
      const csvFile = new File([csv], 'preview.csv', { type: 'text/csv' });
      const csvRows = await readExcelRowsForExtract(csvFile);
      const csvSkuCount = countVendorSkuRows(csvRows);
      const consolidationOrders = tryExtractConsolidationOrders(csvRows, 'preview.csv');
      const hasConsolidation = (consolidationOrders?.length ?? 0) > 0;

      // RE&S consolidation: body table wins over a useless PDF cover (avoids long AI PDF pass).
      // Do NOT apply this to ordinary customers who attach a real PDF/Excel PO.
      if (hasConsolidation && (!hasPdfAttachment || consolidationHint)) {
        const bodyFile = emailBodyCsvFile(parsed.subject ?? '', csv);
        // Show the consolidation table (Item / Item Name / Delivery Date), not the HTML shell.
        if (preview?.kind === 'html' || preview?.kind === 'pdf') {
          URL.revokeObjectURL(preview.url);
        }
        return {
          file: bodyFile,
          preview: await buildPoSourcePreview(bodyFile),
        };
      }

      // Real PDF PO attachment → always prefer it (MBS/Ariba HTML-only emails have no PDF).
      if (hasPdfAttachment) {
        return { file: resolved.file, preview };
      }

      let attachmentSkuCount = 0;
      if (parsed.attachments.length > 0 && hasExcelAttachment) {
        const attachmentRows = await readExcelRowsForExtract(resolved.file);
        attachmentSkuCount = countVendorSkuRows(attachmentRows);
      }

      if (hasExcelAttachment && attachmentSkuCount > Math.max(csvSkuCount, htmlSkuCount)) {
        return { file: resolved.file, preview };
      }

      // No PDF: HTML body tables (MBS / Paradise / FoodMarketHub) are the extract source.
      if (csvSkuCount >= 3 || (hasConsolidation && !hasExcelAttachment)) {
        return { file: emailBodyCsvFile(parsed.subject ?? '', csv), preview };
      }

      if (hasExcelAttachment) {
        return { file: resolved.file, preview };
      }
    }

    // Plain-text body with a consolidation table (no HTML tables).
    if (parsed.bodyText?.trim()) {
      const rows = plainTextToTableRows(parsed.bodyText);
      const consolidationOrders = tryExtractConsolidationOrders(rows, 'email-body.txt');
      if (consolidationOrders && consolidationOrders.length > 0) {
        // Same rule: never discard a PDF PO for a plain-text consolidation guess.
        if (!hasPdfAttachment || consolidationHint) {
          const csv = rows
            .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
            .join('\n');
          return { file: emailBodyCsvFile(parsed.subject ?? '', csv), preview };
        }
      }
    }

    if (preview) return { file: resolved.file, preview };

    // Plain OCR / email-body .txt used for extract — still show a neat card up front.
    const resolvedExt = resolved.file.name.split('.').pop()?.toLowerCase() ?? '';
    if (resolvedExt === 'txt') {
      const text = await resolved.file.text();
      return {
        file: resolved.file,
        preview: buildEmailTextPreview(text, parsed.subject),
      };
    }

    return { file: resolved.file };
  }
  return { file };
}
